import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const DOCUMENT_LAST_ROUTE_STORAGE_KEY = "documents-last-route";
const DOCUMENT_PENDING_RESTORE_STORAGE_KEY = "documents-pending-restore-route";
const DOCUMENT_ROOT_PATH = "/dashboard/mes-documents";

export const clearPersistedDocumentRoute = () => {
  localStorage.removeItem(DOCUMENT_LAST_ROUTE_STORAGE_KEY);
  sessionStorage.removeItem(DOCUMENT_PENDING_RESTORE_STORAGE_KEY);
};

export const usePersistDocumentRoute = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialRouteCheck = useRef(true);
  const [isRestoringDocumentRoute, setIsRestoringDocumentRoute] = useState(() => {
    const isDocumentRoot =
      window.location.pathname === DOCUMENT_ROOT_PATH && !window.location.search;
    const pendingRoute = sessionStorage.getItem(DOCUMENT_PENDING_RESTORE_STORAGE_KEY);
    const storedRoute = localStorage.getItem(DOCUMENT_LAST_ROUTE_STORAGE_KEY);
    const routeToRestore = pendingRoute || storedRoute;

    return Boolean(
      isDocumentRoot &&
        routeToRestore &&
        routeToRestore !== `${window.location.pathname}${window.location.search}` &&
        routeToRestore.startsWith(DOCUMENT_ROOT_PATH),
    );
  });

  useLayoutEffect(() => {
    const currentRoute = `${location.pathname}${location.search}`;
    const isDocumentRoute = location.pathname.startsWith(DOCUMENT_ROOT_PATH);
    const isDocumentRoot = location.pathname === DOCUMENT_ROOT_PATH && !location.search;

    if (!isDocumentRoute) {
      setIsRestoringDocumentRoute(false);
      isInitialRouteCheck.current = false;
      return;
    }

    if (isDocumentRoot) {
      const pendingRoute = sessionStorage.getItem(DOCUMENT_PENDING_RESTORE_STORAGE_KEY);
      const storedRoute = isInitialRouteCheck.current
        ? localStorage.getItem(DOCUMENT_LAST_ROUTE_STORAGE_KEY)
        : null;
      const routeToRestore = pendingRoute || storedRoute;
      sessionStorage.removeItem(DOCUMENT_PENDING_RESTORE_STORAGE_KEY);

      if (
        routeToRestore &&
        routeToRestore !== currentRoute &&
        routeToRestore.startsWith(DOCUMENT_ROOT_PATH)
      ) {
        setIsRestoringDocumentRoute(true);
        navigate(routeToRestore, { replace: true });
        isInitialRouteCheck.current = false;
        return;
      }

      setIsRestoringDocumentRoute(false);
      isInitialRouteCheck.current = false;
      return;
    }

    localStorage.setItem(DOCUMENT_LAST_ROUTE_STORAGE_KEY, currentRoute);
    setIsRestoringDocumentRoute(false);
    isInitialRouteCheck.current = false;
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const currentRoute = `${location.pathname}${location.search}`;

    const handleBeforeUnload = () => {
      const isDocumentRoute = location.pathname.startsWith(DOCUMENT_ROOT_PATH);
      const isDocumentRoot = location.pathname === DOCUMENT_ROOT_PATH && !location.search;

      if (isDocumentRoute && !isDocumentRoot) {
        sessionStorage.setItem(DOCUMENT_PENDING_RESTORE_STORAGE_KEY, currentRoute);
        localStorage.setItem(DOCUMENT_LAST_ROUTE_STORAGE_KEY, currentRoute);
        return;
      }

      sessionStorage.removeItem(DOCUMENT_PENDING_RESTORE_STORAGE_KEY);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handleBeforeUnload);
    };
  }, [location.pathname, location.search]);

  return isRestoringDocumentRoute;
};
